import {
  CardTable,
  type CardColumn,
  type TColumnsPerRow,
} from '@/components/CardTable';
import {
  InvoiceTransactionColumn,
  InvoiceTransactionRow,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoiceTransactionColumns';
import { TRANSACTION_MASTER_FIELD_NAMES } from '@/features/transaction_items/constants';
import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { FC, memo, ReactNode, useCallback, useMemo } from 'react';
import styles from './TransactionsDetailCards.module.scss';

export type Props = {
  columns: InvoiceTransactionColumn[];
  data: InvoiceTransactionRow[];
  /**
   * Grand total of transactions in the report — the counter's denominator.
   * NOT `data.length`: rows load incrementally, so `data` only holds what's
   * been fetched so far, whereas the counter must read 「1/2000件」 from the
   * first card, matching the count shown in the section's ControlArea.
   * Defaults to `data.length` when omitted (e.g. in isolated tests).
   */
  totalRecords?: number;
  /** Action placed below the card list (e.g. 「残りをすべて表示」). */
  footer?: ReactNode;
  /**
   * Card columns per row — 6 when there is no invoice preview shown (left
   * content full width), 3 when the preview is shown.
   * @default 3
   */
  columnsPerRow?: TColumnsPerRow;
};

/**
 * Card rendering of 支払明細, sharing the table's column definitions so a new
 * office custom field shows up in both layouts without a second definition.
 */
export const TransactionsDetailCards: FC<Props> = memo(
  ({ columns, data, totalRecords, footer, columnsPerRow = 3 }) => {
    const { t } = useTranslation();

    const counterTotal = totalRecords ?? data.length;

    // 明細番号 becomes the card heading, so it is not repeated inside the grid.
    const cardColumns = useMemo<CardColumn<InvoiceTransactionRow>[]>(
      () =>
        columns.filter(
          (column) => column.dataIndex !== TRANSACTION_MASTER_FIELD_NAMES.NUMBER
        ),
      [columns]
    );

    const renderHeader = useCallback(
      // `index` is the row's global position (rows load from offset 0 upward,
      // so the loaded-array index is the global index). The denominator is the
      // report's grand total, not the number of cards currently rendered.
      (record: InvoiceTransactionRow, index: number) => ({
        title: t('approving_invoice_reports.transactions.card_number', {
          number: record.number ?? '',
        }),
        counter: t('approving_invoice_reports.transactions.card_counter', {
          index: toCommaSeparated(index + 1),
          total: toCommaSeparated(counterTotal),
        }),
      }),
      [t, counterTotal]
    );

    return (
      <div className={styles.container} data-testid='transactions-detail-cards'>
        <CardTable<InvoiceTransactionRow>
          columns={cardColumns}
          data={data}
          columnsPerRow={columnsPerRow}
          header={renderHeader}
          emptyMessage={t('transactions_detail_empty_message')}
          stickyHeader={true}
          windowScroll={true}
          testId='transactions-detail-cards'
          footer={footer}
        />
      </div>
    );
  }
);
TransactionsDetailCards.displayName = 'TransactionsDetailCards';
